import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Icon,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  API_METHOD_GET,
  API_METHOD_POST,
  ENTITY_STATUS,
} from "../../config/constants";
import {
  FETCH_DOC_JOB_ERROR,
  FETCH_DOC_JOB_LOADING,
  FETCH_DOC_JOB_SUCCESS,
  FETCH_JOB_ERROR,
  FETCH_JOB_SUCCESS,
  MSME_ADD_CONV_ERROR,
  MSME_ADD_CONV_LOADING,
  MSME_ADD_CONV_SUCCESS,
  MSME_DATA_ERROR,
  MSME_DATA_LOADING,
  MSME_DATA_SUCCESS,
  MSME_GET_CONV_ERROR,
  MSME_GET_CONV_LOADING,
  MSME_GET_CONV_SUCCESS,
  SET_BILL_REDUCER,
} from "../../store/types/billTypes";
import { useDispatch, useSelector } from "react-redux";
import { api_request } from "../../store/actions/commonAction";
import FullPageLoader from "../../core/Loader/FullPageLoader";
import CoreSelect from "../../core/inputs/CoreSelect";

const STATUS_META = {
  Verified: { label: "Verified", color: "success", icon: "check_circle" },
  Pending: { label: "Pending", color: "warning", icon: "schedule" },
  Missing: { label: "Missing", color: "error", icon: "error" },
};

const REQUEST_STATUS_META = {
  draft: { label: "Draft", color: "default" },
  sent: { label: "Sent", color: "info" },
  received: { label: "Received", color: "success" },
};

const TONE_META = {
  Cooperative: {
    icon: "handshake",
    color: "success.main",
    label: "Cooperative",
  },
  Neutral: { icon: "forum", color: "text.secondary", label: "Neutral" },
  Escalated: { icon: "priority_high", color: "error.main", label: "Escalated" },
};

const getToneMeta = (tone) => {
  return TONE_META[tone] || TONE_META.Neutral;
};

const validEmail = (email) => {
  if (typeof email !== "string") {
    return false;
  }

  const trimmedEmail = email.trim();

  if (!trimmedEmail) {
    return false;
  }

  // Practical email validation for UI filtering.
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return emailRegex.test(trimmedEmail);
};

const getUniqueMails = (conversations) => {
  let emails = new Set();

  if (!Array.isArray(conversations)) {
    return [];
  }

  conversations.forEach((mail) => {
    if (mail.Sender) emails.add(mail.Sender);
    if (mail.Receiver) {
      if (mail?.Receiver?.includes(",")) {
        mail.Receiver.split(",").forEach((email) => emails.add(email.trim()));
      } else emails.add(mail.Receiver);
    }
  });
  console.log(
    "Unique emails extracted for reciepients",
    emails,
    Array.from(emails).map((email) => ({
      label: email,
      value: email,
    })),
  );
  return Array.from(emails)
    .map((email) => ({
      label: email,
      value: email,
    }))
    .filter((email) => validEmail(email.value));
};

const MsmeDashboard = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [customRequest, setCustomRequest] = useState("");
  const [nextMail, setNextMail] = useState("");
  const [jobId, setJobId] = useState(null);
  const [disputePopover, setDisputePopover] = useState({
    anchorEl: null,
    description: "",
  });
  const uid = useSelector((state) => state?.auth?.uid);
  const dashboardData = useSelector((state) => state?.bill?.msmeDashboardData);
  const addConvSuccess = useSelector(
    (state) => state?.bill?.msmeAddConvSuccess,
  );
  const requestQueue = useSelector((state) => state?.bill?.msmeConversations);
  const docJobs = useSelector((state) => state?.bill?.docJobs?.rows);

  const CASE_OVERVIEW = dashboardData?.summary || {};
  const DOCUMENTS = dashboardData?.docList || [];
  const MAIL_TIMELINE = dashboardData?.mail?.conversations || [];
  const DISPUTE_POINTS = dashboardData?.disputes || [];
  // const stat
  console.log("dashboard data in component", dashboardData);

  useEffect(() => {
    getJobs();
    return () => {
      dispatch({
        type: SET_BILL_REDUCER,
        payload: { msmeDashboardData: null, msmeConversations: [] },
      });
    };
  }, []);

  useEffect(() => {
    if (jobId) {
      getInitialData();
      getConversations();
    }
  }, [jobId]);

  useEffect(() => {
    if (addConvSuccess) {
      getConversations();
      dispatch({
        type: SET_BILL_REDUCER,
        payload: { msmeAddConvSuccess: false },
      });
    }
  }, [addConvSuccess]);

  const getJobs = () => {
    dispatch(
      api_request(
        API_METHOD_GET,
        "/business/all/Jobs",
        {},
        {
          _defaultFilter: encodeURIComponent(
            JSON.stringify({
              toolType: "case-processor",
              type: "bulk-doc-processing",
              _status: ENTITY_STATUS.completed,
              createdBy: uid,
            }),
          ),
        },
        FETCH_DOC_JOB_LOADING,
        FETCH_DOC_JOB_SUCCESS,
        FETCH_DOC_JOB_ERROR,
        true,
        false,
        false,
      ),
    );
  };

  const getInitialData = () => {
    dispatch(
      api_request(
        API_METHOD_GET,
        "/api/msme/data",
        {},
        {
          jobId: jobId, // Replace with actual job ID as needed
        },
        MSME_DATA_LOADING,
        MSME_DATA_SUCCESS,
        MSME_DATA_ERROR,
        true,
        false,
        true,
      ),
    );
  };

  const getConversations = () => {
    dispatch(
      api_request(
        API_METHOD_GET,
        "/api/msme/conversations",
        {},
        {
          jobId: jobId, // Replace with actual job ID as needed
        },
        MSME_GET_CONV_LOADING,
        MSME_GET_CONV_SUCCESS,
        MSME_GET_CONV_ERROR,
        true,
        false,
        false,
      ),
    );
  };

  const metrics = useMemo(() => {
    const verifiedCount = DOCUMENTS.filter(
      (doc) => doc.status === "Verified",
    ).length;
    const pendingCount = DOCUMENTS.filter(
      (doc) => doc.status === "Pending",
    ).length;
    const missingCount = DOCUMENTS.filter(
      (doc) => doc.status === "Missing",
    ).length;
    const actionRequiredMails = MAIL_TIMELINE.filter(
      (mail) => mail["Action Required"] === "yes",
    ).length;

    const evidenceCompleteness = Math.round(
      (verifiedCount / DOCUMENTS.length) * 100,
    );
    const confidenceScore = Math.round(
      DOCUMENTS.reduce((acc, doc) => acc + doc.confidence, 0) /
        DOCUMENTS.length,
    );

    return {
      verifiedCount,
      pendingCount,
      missingCount,
      actionRequiredMails,
      evidenceCompleteness,
      confidenceScore,
    };
  }, [dashboardData]);

  const pendingRequests = useMemo(() => {
    return requestQueue?.filter((request) => request.status !== "received")
      .length;
  }, [requestQueue]);

  // const onCycleRequestStatus = (requestId) => {
  //   setRequestQueue((prev) =>
  //     prev.map((request) => {
  //       if (request.id !== requestId) {
  //         return request;
  //       }

  //       return {
  //         ...request,
  //         status: getRequestNextState(request.status),
  //       };
  //     }),
  //   );
  // };

  const onAddCustomRequest = () => {
    const nextValue = customRequest.trim();

    if (!nextValue) {
      return;
    }
    dispatch(
      api_request(
        API_METHOD_POST,
        "/api/msme/conversation/add",
        {
          message: nextValue,
          reciepients: [{ email: nextMail }],
          jobId: jobId,
          data: dashboardData,
        },
        {},
        MSME_ADD_CONV_LOADING,
        MSME_ADD_CONV_SUCCESS,
        MSME_ADD_CONV_ERROR,
        true,
        false,
        true,
      ),
    );
  };

  const handleDisputePopoverOpen = (event, description) => {
    setDisputePopover({
      anchorEl: event.currentTarget,
      description: description || "No description available.",
    });
  };

  const handleDisputePopoverClose = () => {
    setDisputePopover({ anchorEl: null, description: "" });
  };

  const isDisputePopoverOpen = Boolean(disputePopover.anchorEl);

  return (
    <Box
      sx={{
        px: 2,
        mb: -1,
        width: "100%",
        minHeight: "calc(100vh - 72px)",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            MSME Dashboard
          </Typography>
          <TextField
            select
            size="small"
            label="Change Case"
            value={jobId}
            onChange={(e) => {
              setJobId(e.target.value);
            }}
            sx={{ minWidth: 140 }}
          >
            {docJobs?.map((job) => (
              <MenuItem key={job.id} value={job.id}>
                {job.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Unified evidence view for startup verification, dispute analysis, and
          decision support.
        </Typography> */}
        {dashboardData && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mt: 1.5, flexWrap: "wrap" }}
          >
            <Chip label={`Case: ${CASE_OVERVIEW.caseId}`} size="small" />
            <Chip label={CASE_OVERVIEW.stage} size="small" />
            <Chip label={CASE_OVERVIEW.disputedParties} size="small" />
            <Chip
              label={`Case registered on: ${CASE_OVERVIEW.startDate}`}
              size="small"
            />
          </Stack>
        )}
      </Box>

      {dashboardData ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Box sx={{ mb: 2 }}>
                <Card sx={{ height: "100%", maxHeight: 250, minHeight: 250 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Documents Submitted
                    </Typography>
                    {/* <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    GST, PAN, MSME certificate, incorporation certificate, and
                    mail bundle are validated side by side for quick
                    adjudication.
                  </Typography> */}
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Document</TableCell>
                            <TableCell>Reference</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Verified By</TableCell>
                            {/* <TableCell>Confidence</TableCell>
                          <TableCell>Key Insight</TableCell> */}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {DOCUMENTS.map((doc) => {
                            const statusMeta =
                              STATUS_META[doc.status] || STATUS_META.pending;

                            return (
                              <TableRow key={doc.id} hover>
                                <TableCell sx={{ fontWeight: 500 }}>
                                  {doc.label}
                                </TableCell>
                                <TableCell>{doc.docNo}</TableCell>
                                <TableCell>
                                  <Chip
                                    icon={<Icon>{statusMeta.icon}</Icon>}
                                    size="small"
                                    label={statusMeta.label}
                                    color={statusMeta.color}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{doc.verifiedBy}</TableCell>
                                {/* <TableCell sx={{ minWidth: 160 }}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Box sx={{ width: 80 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={doc.confidence}
                                      color={
                                        doc.confidence > 85
                                          ? "success"
                                          : "warning"
                                      }
                                      sx={{ height: 8, borderRadius: 4 }}
                                    />
                                  </Box>
                                  <Typography variant="body2">
                                    {doc.confidence}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {doc.insight}
                                </Typography>
                              </TableCell> */}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>

              <Box>
                <Card sx={{ maxHeight: 250, minHeight: 250 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Past Conversations
                    </Typography>

                    <List
                      sx={{
                        py: 0,
                        maxHeight: 250,
                        minHeight: 250,
                        overflowY: "auto",
                      }}
                    >
                      {MAIL_TIMELINE.map((mail, index) => {
                        const toneMeta = getToneMeta(mail.Tone);

                        return (
                          <React.Fragment key={mail.id}>
                            <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 34, mt: 0.5 }}>
                                <Icon sx={{ color: toneMeta.color }}>
                                  {toneMeta.icon}
                                </Icon>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1}
                                    alignItems={{
                                      xs: "flex-start",
                                      md: "center",
                                    }}
                                  >
                                    <Typography variant="subtitle2">
                                      {mail.subject}
                                    </Typography>
                                    <Chip
                                      label={mail.Date}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      size="small"
                                      color={
                                        mail["Action Required"] == "yes"
                                          ? "warning"
                                          : "success"
                                      }
                                      label={
                                        mail["Action Required"] == "yes"
                                          ? "Action required"
                                          : "No pending action"
                                      }
                                    />
                                    <Chip
                                      size="small"
                                      label={toneMeta.label}
                                      color={
                                        mail.Tone === "Escalated"
                                          ? "error"
                                          : "default"
                                      }
                                    />
                                  </Stack>
                                }
                                secondary={
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {mail.Sender} → {mail.Receiver}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {mail.Summary}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index !== MAIL_TIMELINE.length - 1 && (
                              <Divider component="li" />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </List>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
            <Grid item xs={12} lg={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Major Disputes
                  </Typography>

                  {/* <Divider sx={{ mb: 1.5 }} /> */}
                  <List
                    dense
                    sx={{
                      py: 0,
                      maxHeight: 447,
                      minHeight: 447,
                      overflowY: "auto",
                    }}
                  >
                    {Array.isArray(DISPUTE_POINTS) &&
                      DISPUTE_POINTS.map((item, i) => (
                        <ListItem
                          key={"dispute-" + i}
                          sx={{ px: 0, alignItems: "flex-start" }}
                        >
                          <ListItemIcon
                            sx={{ minWidth: 30, mt: 0.3 }}
                            onMouseEnter={(event) =>
                              handleDisputePopoverOpen(event, item.description)
                            }
                            onMouseLeave={handleDisputePopoverClose}
                          >
                            <Icon
                              color={
                                item.severity === "high" ? "error" : "warning"
                              }
                              fontSize="small"
                              sx={{ cursor: "pointer" }}
                            >
                              report_problem
                            </Icon>
                          </ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            secondary={`Source: ${item.source}`}
                          />
                        </ListItem>
                      ))}
                  </List>

                  <Popover
                    open={isDisputePopoverOpen}
                    anchorEl={disputePopover.anchorEl}
                    onClose={handleDisputePopoverClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    disableRestoreFocus
                    slotProps={{
                      paper: {
                        onMouseEnter: () => undefined,
                        onMouseLeave: handleDisputePopoverClose,
                        sx: {
                          maxWidth: 320,
                          p: 1.5,
                          pointerEvents: "auto",
                        },
                      },
                    }}
                    sx={{ pointerEvents: "none" }}
                  >
                    <Typography variant="body2">
                      {disputePopover.description}
                    </Typography>
                  </Popover>

                  {/* <Alert
                    severity="warning"
                    sx={{ mt: 2, alignItems: "center" }}
                    icon={<Icon>gavel</Icon>}
                  >
                    Decision recommendation: Request one additional MSME proof
                    and final dispatch evidence before issuing final order.
                  </Alert> */}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={3}>
              <Card sx={{ maxHeight: 520, minHeight: 520 }}>
                <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                  <Stack direction="column" spacing={1}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Send Notification
                    </Typography>
                    <CoreSelect
                      fullWidth
                      sx={{ marginTop: 0 }}
                      label="Reciepient"
                      size="small"
                      options={getUniqueMails(MAIL_TIMELINE || [])}
                      handleChange={(event) => {
                        console.log(
                          "Selected mail for request",
                          event.target.value,
                        );
                        setNextMail(event.target.value);
                      }}
                    />
                    <TextField
                      fullWidth
                      sx={{ height: 100 }}
                      // size="large"
                      multiline={true}
                      rows={3}
                      label="Please type"
                      value={customRequest}
                      onChange={(event) => setCustomRequest(event.target.value)}
                    />
                    <Box
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button variant="contained" onClick={onAddCustomRequest}>
                        Send
                      </Button>
                    </Box>
                  </Stack>

                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Notification History <Icon>notification</Icon>
                  </Typography>
                  <Divider />
                  <List
                    dense
                    sx={{
                      maxHeight: 250,
                      minHeight: 250,
                      overflowY: "auto",
                      py: 0,
                    }}
                  >
                    {requestQueue.map((request) => {
                      const meta = REQUEST_STATUS_META[request.status];

                      return (
                        <ListItem
                          key={request.id}
                          alignItems="flex-start"
                          sx={{
                            px: 0,
                            py: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                          }}
                          // secondaryAction={
                          //   <Button
                          //     size="small"
                          //     onClick={() => onCycleRequestStatus(request.id)}
                          //   >
                          //     Mark {getRequestNextState(request.status)}
                          //   </Button>
                          // }
                        >
                          <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
                            <Icon fontSize="small" color="action">
                              help_outline
                            </Icon>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ pr: 7 }}>
                                {request.body}
                              </Typography>
                            }
                            secondary={
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 0.75 }}
                              >
                                <Chip
                                  size="small"
                                  label={request.subject}
                                  variant="outlined"
                                />
                                {/* <Chip
                                size="small"
                                label={meta?.label}
                                color={meta?.color}
                              /> */}
                              </Stack>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1.5, display: "block" }}
          >
            Startup: {CASE_OVERVIEW.startupName} | District:{" "}
            {CASE_OVERVIEW.district}
            {" | "}
            Analyst: {CASE_OVERVIEW.analyst}
          </Typography>
        </>
      ) : (
        <Box
          style={{
            minHeight: "calc(100vh - 68px - 200px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Select a case to view dashboard
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MsmeDashboard;
